/**
 * ImageRenderer Module
 * Handles canvas operations, image rendering, and export functionality
 */
import type { RenderConfig, ScaleMode, ExportFormat } from '../../types';
import { downloadBlob } from '../../utils/downloadHelper';

export class ImageRenderer {
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    backgroundImage: HTMLImageElement | null;

    constructor(canvasId: string = 'canvas') {
        const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
        if (!canvas) {
            throw new Error('Canvas element not found');
        }
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('2D context not supported');
        }
        
        this.canvas = canvas;
        this.ctx = ctx;
        this.backgroundImage = null;
    }

    /**
     * Set background image for rendering
     * @param img - Image element to use as background
     */
    setBackgroundImage(img: HTMLImageElement): void {
        this.backgroundImage = img;
    }

    /**
     * Remove background image
     */
    removeBackgroundImage(): void {
        this.backgroundImage = null;
    }

    /**
     * Check if background image is set
     * @returns True if background image is set
     */
    hasBackgroundImage(): boolean {
        return this.backgroundImage !== null;
    }

    /**
     * Render the complete image with background and text
     * @param config - Rendering configuration
     */
    renderImage(config: RenderConfig): void {
        const {
            width,
            height,
            backgroundColor,
            text,
            textColor,
            fontSize,
            fontFamily,
            scaleMode = 'fill'
        } = config;

        // Set canvas dimensions
        this.canvas.width = width;
        this.canvas.height = height;

        // Clear canvas
        this.ctx.clearRect(0, 0, width, height);

        // Draw background
        this.drawBackground(backgroundColor, width, height);

        // Draw background image if available
        if (this.backgroundImage) {
            this.drawBackgroundImage(this.backgroundImage, scaleMode, width, height);
        }

        // Draw text if provided, otherwise show placeholder
        if (text && text.trim()) {
            this.drawText(text, textColor, fontSize, fontFamily, width, height, false);
        } else {
            // Show placeholder text with dimensions
            this.drawPlaceholderText(width, height, fontSize, fontFamily);
        }
    }

    /**
     * Draw solid background color
     * @param backgroundColor - Background color
     * @param width - Canvas width
     * @param height - Canvas height
     */
    drawBackground(backgroundColor: string, width: number, height: number): void {
        this.ctx.fillStyle = backgroundColor;
        this.ctx.fillRect(0, 0, width, height);
    }

    /**
     * Draw background image with specified scaling mode
     * @param img - Image to draw
     * @param scaleMode - How to scale the image ('fill', 'fit', 'stretch')
     * @param canvasWidth - Canvas width
     * @param canvasHeight - Canvas height
     */
    drawBackgroundImage(img: HTMLImageElement, scaleMode: ScaleMode, canvasWidth: number, canvasHeight: number): void {
        const imgAspect = img.width / img.height;
        const canvasAspect = canvasWidth / canvasHeight;
        
        let drawWidth: number, drawHeight: number, drawX: number, drawY: number;

        switch (scaleMode) {
            case 'fill':
                // Cover the entire canvas, may crop image
                if (imgAspect > canvasAspect) {
                    drawHeight = canvasHeight;
                    drawWidth = drawHeight * imgAspect;
                    drawX = (canvasWidth - drawWidth) / 2;
                    drawY = 0;
                } else {
                    drawWidth = canvasWidth;
                    drawHeight = drawWidth / imgAspect;
                    drawX = 0;
                    drawY = (canvasHeight - drawHeight) / 2;
                }
                break;
                
            case 'fit':
                // Fit entire image within canvas, may have empty space
                if (imgAspect > canvasAspect) {
                    drawWidth = canvasWidth;
                    drawHeight = drawWidth / imgAspect;
                    drawX = 0;
                    drawY = (canvasHeight - drawHeight) / 2;
                } else {
                    drawHeight = canvasHeight;
                    drawWidth = drawHeight * imgAspect;
                    drawX = (canvasWidth - drawWidth) / 2;
                    drawY = 0;
                }
                break;
                
            case 'stretch':
                // Stretch to fill canvas exactly
                drawWidth = canvasWidth;
                drawHeight = canvasHeight;
                drawX = 0;
                drawY = 0;
                break;
                
            case 'tile':
                // Create a repeating pattern
                const pattern = this.ctx.createPattern(img, 'repeat');
                if (pattern) {
                    this.ctx.fillStyle = pattern;
                    this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);
                }
                return; // Skip the drawImage call below
                
            default:
                drawWidth = canvasWidth;
                drawHeight = canvasHeight;
                drawX = 0;
                drawY = 0;
        }

        this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    }

    /**
     * Draw text on canvas
     * @param text - Text to draw
     * @param textColor - Text color
     * @param fontSize - Font size in pixels
     * @param fontFamily - Font family
     * @param canvasWidth - Canvas width
     * @param canvasHeight - Canvas height
     * @param addStroke - Whether to add stroke for visibility
     */
    drawText(text: string, textColor: string, fontSize: number, fontFamily: string, canvasWidth: number, canvasHeight: number, addStroke: boolean = false): void {
        this.ctx.fillStyle = textColor;
        this.ctx.font = `${fontSize}px ${fontFamily}`;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        
        // Calculate text position (centered)
        const x = canvasWidth / 2;
        const y = canvasHeight / 2;
        
        // Add stroke only if requested (for visibility on complex backgrounds)
        if (addStroke) {
            this.ctx.strokeStyle = this.getContrastColor(textColor);
            this.ctx.lineWidth = Math.max(1, fontSize / 20);
            this.ctx.strokeText(text, x, y);
        }
        
        this.ctx.fillText(text, x, y);
    }

    /**
     * Draw placeholder text showing image dimensions
     * @param width - Canvas width
     * @param height - Canvas height
     * @param fontSize - Font size
     * @param fontFamily - Font family
     */
    drawPlaceholderText(width: number, height: number, fontSize: number, fontFamily: string): void {
        const placeholderText = `${width} × ${height}`;
        
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        this.ctx.font = `${fontSize}px ${fontFamily}`;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        
        // Add stroke for better visibility on any background
        this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
        this.ctx.lineWidth = Math.max(1, fontSize / 20);
        this.ctx.strokeText(placeholderText, width / 2, height / 2);
        this.ctx.fillText(placeholderText, width / 2, height / 2);
    }

    /**
     * Get a contrasting color for text stroke
     * @param color - Original color
     * @returns Contrasting color
     */
    getContrastColor(color: string): string {
        // Convert hex to RGB
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);
        
        // Calculate luminance
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        
        // Return black or white based on luminance
        return luminance > 0.5 ? '#000000' : '#ffffff';
    }

    /**
     * Export canvas as blob
     * @param format - Image format ('png', 'jpeg', 'webp')
     * @param quality - Image quality (0-1, for lossy formats)
     * @returns Promise resolving to image blob
     */
    exportAsBlob(format: ExportFormat = 'png', quality: number = 0.9): Promise<Blob | null> {
        return new Promise((resolve) => {
            this.canvas.toBlob((blob) => resolve(blob), this.getMimeType(format), quality);
        });
    }

    /**
     * Export canvas as data URL
     * @param format - Image format ('png', 'jpeg', 'webp')
     * @param quality - Image quality (0-1, for lossy formats)
     * @returns Data URL
     */
    exportAsDataURL(format: ExportFormat = 'png', quality: number = 0.9): string {
        return this.canvas.toDataURL(this.getMimeType(format), quality);
    }

    /**
     * Get MIME type for format
     * @param format - Format name
     * @returns MIME type
     */
    getMimeType(format: string): string {
        const mimeTypes: Record<string, string> = {
            'png': 'image/png',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'webp': 'image/webp',
            'bmp': 'image/bmp'
        };
        return mimeTypes[format.toLowerCase()] || 'image/png';
    }

    /**
     * Download canvas as file
     * @param filename - Filename for download
     * @param format - Image format
     * @param quality - Image quality
     */
    async downloadImage(filename: string, format: ExportFormat = 'png', quality: number = 0.9): Promise<{ success: boolean; fileSize: number; format: string }> {
        try {
            const blob = await this.exportAsBlob(format, quality);
            if (!blob) {
                throw new Error('Failed to create blob');
            }
            
            // Use cross-browser compatible download helper
            downloadBlob(blob, filename);
            
            return {
                success: true,
                fileSize: blob.size,
                format: format
            };
        } catch (error) {
            throw new Error(`Download failed: ${(error as Error).message}`);
        }
    }

    /**
     * Get canvas dimensions
     * @returns Canvas width and height
     */
    getDimensions(): { width: number; height: number } {
        return {
            width: this.canvas.width,
            height: this.canvas.height
        };
    }

    /**
     * Clear the canvas
     */
    clear(): void {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
}
